/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toET } from '../utils/et';
import { format, startOfWeek, endOfWeek, subWeeks, addWeeks } from 'date-fns';

const user = JSON.parse(localStorage.getItem('nemt_user') || '{}');
const isDispatcher = user.role === 'dispatcher';

function Badge({ bg, color, label }) {
  return (
    <span style={{
      background: bg, color, fontSize: 10, padding: '2px 7px',
      borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap'
    }}>{label}</span>
  );
}

function currency(val) {
  return '$' + parseFloat(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function DriverRow({ driver, expanded, onToggle, weekStart, weekEnd, onRefresh }) {
  const hasPay = parseFloat(driver.total_pay_with_salary || driver.total_pay) > 0;
  const hasHours = parseFloat(driver.total_hours) > 0;
  const hasSalary = parseFloat(driver.salary) > 0;
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    if (expanded) {
      setLoadingTrips(true);
      api.get(`/payroll/driver/${driver.driver_id}?startDate=${weekStart}&endDate=${weekEnd}`)
        .then(res => setTrips(res.data.trips || []))
        .catch(() => {})
        .finally(() => setLoadingTrips(false));
    }
  }, [expanded, driver.driver_id, weekStart, weekEnd]);

  const displayTotal = parseFloat(driver.total_pay_with_salary || driver.total_pay || 0);

  return (
    <div style={{ borderBottom: '0.5px solid #f0f0f0' }}>
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', cursor: 'pointer',
          background: expanded ? '#f8f8f8' : 'white',
          opacity: !hasPay ? 0.5 : 1,
        }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          background: hasPay ? '#E1F5EE' : '#f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 500,
          color: hasPay ? '#0F6E56' : '#999',
        }}>
          {driver.first_name?.[0]}{driver.last_name?.[0]}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>
            {driver.first_name} {driver.last_name}
            {hasSalary && <Badge bg="#E6F1FB" color="#185FA5" label="Salary" />}
          </div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 1 }}>
            {hasSalary && `$${parseFloat(driver.salary).toLocaleString()} salary`}
            {hasSalary && hasHours && ' + '}
            {hasHours && `${driver.trip_count} trips · ${parseFloat(driver.total_hours).toFixed(2)} hrs`}
            {!hasSalary && !hasHours && 'No activity'}
            {parseFloat(driver.total_meal_reimbursement) > 0 && ' · meal reimbursement'}
            {parseFloat(driver.total_cleanup_bonus) > 0 && ' · cleanup bonus'}
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: hasPay ? '#185FA5' : '#999' }}>
            {currency(displayTotal)}
          </div>
          {hasSalary && hasHours ? (
            <div style={{ fontSize: 10, color: '#999' }}>
              salary + trips
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#999' }}>${driver.hourly_rate}/hr</div>
          )}
        </div>

        <div style={{ fontSize: 12, color: '#aaa', marginLeft: 4 }}>
          {expanded ? '▲' : '▼'}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '10px 14px 14px 56px', background: '#fafafa' }}>
          {/* Trip breakdown */}
          {loadingTrips ? (
            <div style={{ fontSize: 12, color: '#999' }}>Loading trips...</div>
          ) : trips.length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Trip breakdown</div>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e0e0db' }}>
                    <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 10, color: '#999', fontWeight: 500 }}>Trip</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 10, color: '#999', fontWeight: 500 }}>Date/Time</th>
                    <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 10, color: '#999', fontWeight: 500 }}>Destination</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: '#999', fontWeight: 500 }}>Miles</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: '#999', fontWeight: 500 }}>Hours</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: '#999', fontWeight: 500 }}>Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {trips.map((trip, i) => editingIdx === i ? (
                    <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0', background: '#FAFAF9' }}>
                      <td style={{ padding: '5px 6px', fontSize: 11, color: '#666' }}>{trip.transport_number}</td>
                      <td style={{ padding: '5px 6px', fontSize: 11, color: '#666' }}>
                        {trip.scheduled_time ? format(toET(trip.scheduled_time), 'EEE h:mm a') : '--'}
                      </td>
                      <td style={{ padding: '5px 6px' }} colSpan={1}>
                        <div style={{ fontSize: 10, color: '#999' }}>Rate</div>
                        <input type="number" step="1" value={editForm.hourlyRate} onChange={e => setEditForm(f => ({...f, hourlyRate: e.target.value}))}
                          style={{ width: 50, fontSize: 11, padding: '2px 4px' }} />
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <div style={{ fontSize: 10, color: '#999' }}>Meal</div>
                        <input type="number" step="1" value={editForm.mealReimbursement} onChange={e => setEditForm(f => ({...f, mealReimbursement: e.target.value}))}
                          style={{ width: 40, fontSize: 11, padding: '2px 4px' }} />
                      </td>
                      <td style={{ padding: '5px 6px' }}>
                        <div style={{ fontSize: 10, color: '#999' }}>Hours</div>
                        <input type="number" step="0.25" value={editForm.billableHours} onChange={e => setEditForm(f => ({...f, billableHours: e.target.value}))}
                          style={{ width: 50, fontSize: 11, padding: '2px 4px' }} />
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right' }}>
                        <button onClick={async () => {
                          try {
                            await api.put(`/payroll/assignee/${trip.assignee_id}`, editForm);
                            setEditingIdx(null);
                            // Reload trips
                            const res = await api.get(`/payroll/driver/${driver.driver_id}?startDate=${weekStart}&endDate=${weekEnd}`);
                            setTrips(res.data.trips || []);
                            if (onRefresh) onRefresh();
                          } catch (e) { alert('Save failed'); }
                        }} style={{ fontSize: 10, padding: '2px 8px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 4, marginRight: 4 }}>Save</button>
                        <button onClick={() => setEditingIdx(null)} style={{ fontSize: 10, padding: '2px 8px' }}>X</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                      <td style={{ padding: '5px 6px', fontSize: 11, color: '#666' }}>{trip.transport_number}</td>
                      <td style={{ padding: '5px 6px', fontSize: 11, color: '#666' }}>
                        {trip.scheduled_time ? format(toET(trip.scheduled_time), 'EEE h:mm a') : '--'}
                      </td>
                      <td style={{ padding: '5px 6px', fontSize: 11, color: '#444', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {(trip.destination_address || '').replace(/,\s*(PA|Pennsylvania)\s*\d+.*$/, '')}
                      </td>
                      <td style={{ padding: '5px 6px', fontSize: 11, color: '#666', textAlign: 'right' }}>
                        {trip.round_trip_miles ? Math.ceil(Number(trip.round_trip_miles)) : '--'}
                      </td>
                      <td style={{ padding: '5px 6px', fontSize: 11, color: '#666', textAlign: 'right' }}>
                        {parseFloat(trip.billable_hours || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '5px 6px', fontSize: 11, textAlign: 'right' }}>
                        <span style={{ fontWeight: 500 }}>{currency(trip.total_pay)}</span>
                        <button onClick={() => {
                          setEditingIdx(i);
                          setEditForm({
                            billableHours: parseFloat(trip.billable_hours || 0).toFixed(2),
                            hourlyRate: parseFloat(trip.hourly_rate || 40),
                            mealReimbursement: parseFloat(trip.meal_reimbursement || 0),
                            cleanupBonus: parseFloat(trip.cleanup_bonus || 0),
                          });
                        }} style={{ fontSize: 9, padding: '1px 5px', marginLeft: 4, color: '#999', background: 'none', border: '0.5px solid #e0e0db', borderRadius: 3, cursor: 'pointer' }}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {/* Summary totals */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, maxWidth: 300, borderTop: '0.5px solid #e0e0db', paddingTop: 8 }}>
            {[
              ...(hasSalary ? [{ label: 'Weekly salary', value: currency(driver.salary) }] : []),
              { label: 'Base pay (trips)', value: currency(driver.total_base_pay) },
              { label: 'Meal reimbursement', value: currency(driver.total_meal_reimbursement) },
              { label: 'Cleanup bonus', value: currency(driver.total_cleanup_bonus) },
              { label: 'Cancellation pay', value: currency(driver.total_cancellation_pay) },
              { label: 'Holiday bonus', value: currency(driver.total_holiday_bonus) },
              { label: 'Vehicle work (work log)', value: currency(driver.work_log_pay) },
              { label: 'Retro adjustments', value: currency(driver.adjustments) },
              { label: 'Total', value: currency(displayTotal), bold: true },
            ].filter(r => r.bold || parseFloat(r.value?.replace(/[$,]/g,'')) !== 0).map(row => (
              <React.Fragment key={row.label}>
                <div style={{ fontSize: 12, color: '#666' }}>{row.label}</div>
                <div style={{ fontSize: 12, fontWeight: row.bold ? 600 : 400, textAlign: 'right', color: row.bold ? '#185FA5' : '#333' }}>
                  {row.value}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Dispatcher Hours Entry (Erika)
// ============================================================
function DispatcherHoursForm({ weekEnd, onSaved }) {
  const [hours, setHours] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [existing, setExisting] = useState(null);

  const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

  useEffect(() => {
    api.get(`/payroll/dispatcher-hours?weekEnding=${weekEndStr}&userEmail=${user.email}`)
      .then(res => {
        const entries = res.data.entries || [];
        if (entries.length > 0) {
          setExisting(entries[0]);
          setHours(String(entries[0].hours));
          setNotes(entries[0].notes || '');
        } else {
          setExisting(null);
          setHours('');
          setNotes('');
        }
      })
      .catch(() => {});
  }, [weekEndStr]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/payroll/dispatcher-hours', {
        userEmail: user.email,
        weekEnding: weekEndStr,
        hours: parseFloat(hours),
        notes: notes || undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      if (onSaved) onSaved();
    } catch (err) {
      alert('Failed to save hours: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Log My Hours</div>
      <div style={{ fontSize: 11, color: '#999', marginBottom: 12 }}>
        Week ending {format(weekEnd, 'MMM d, yyyy')} &middot; $25/hr
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Hours worked</label>
          <input
            type="number" step="0.5" min="0" max="80" required
            value={hours} onChange={e => setHours(e.target.value)}
            style={{ width: 80, fontSize: 13, padding: '6px 8px' }}
            placeholder="0"
          />
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 3 }}>Notes (optional)</label>
          <input
            type="text" value={notes} onChange={e => setNotes(e.target.value)}
            style={{ width: '100%', fontSize: 13, padding: '6px 8px' }}
            placeholder="e.g. Mon-Fri dispatch"
          />
        </div>
        <button type="submit" disabled={saving || !hours}
          style={{
            fontSize: 13, padding: '7px 16px', background: '#185FA5', color: 'white',
            border: 'none', borderRadius: 8, fontWeight: 500, opacity: saving ? 0.6 : 1,
          }}>
          {saving ? 'Saving...' : existing ? 'Update' : 'Submit'}
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: '#1D9E75', fontWeight: 500 }}>Saved!</span>
        )}
      </form>
      {existing && (
        <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>
          Previously logged: {existing.hours} hrs = {currency(existing.total_pay)}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main Payroll Page
// ============================================================
export default function Payroll() {
  const [weekStart, setWeekStart] = useState(
    startOfWeek(toET(new Date()), { weekStartsOn: 1 })
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [nachaLoading, setNachaLoading] = useState(false);

  const fetchPayroll = async () => {
    setLoading(true);
    try {
      const dateStr = format(weekStart, 'yyyy-MM-dd');
      const res = await api.get(`/payroll/week?date=${dateStr}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayroll(); }, [weekStart]);

  const toggleExpanded = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  const activeDrivers = data?.drivers?.filter(d =>
    parseFloat(d.total_hours) > 0 || parseFloat(d.salary) > 0
  ) || [];
  const totalPayout = parseFloat(data?.totalPayout || 0);
  const dispatcherHours = data?.dispatcherHours || [];

  const handleExportNacha = async () => {
    setNachaLoading(true);
    try {
      const dateStr = format(weekStart, 'yyyy-MM-dd');
      const res = await api.get('/payroll/export-nacha?date=' + dateStr, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACH_payroll_${dateStr}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.response?.data?.error || 'Export failed. Make sure bank info is set up.';
      alert(msg);
    } finally {
      setNachaLoading(false);
    }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Payroll</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
            Week of {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={() => setWeekStart(w => subWeeks(w, 1))} style={{ fontSize: 12, padding: '4px 10px' }}>
            ← Prev
          </button>
          <button onClick={() => setWeekStart(startOfWeek(toET(new Date()), { weekStartsOn: 1 }))} style={{ fontSize: 12, padding: '4px 10px' }}>
            This week
          </button>
          <button onClick={() => setWeekStart(w => addWeeks(w, 1))} style={{ fontSize: 12, padding: '4px 10px' }}>
            Next →
          </button>
        </div>
      </div>

      {/* Dispatcher hours entry — only shows for dispatchers (Erika) */}
      {isDispatcher && (
        <DispatcherHoursForm weekEnd={weekEnd} onSaved={fetchPayroll} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Total payout', value: currency(totalPayout), color: '#A32D2D' },
          { label: 'People paid', value: activeDrivers.length + dispatcherHours.length, color: '#185FA5' },
          { label: 'Total trips', value: data?.totalTrips || 0, color: '#1D9E75' },
        ].map(s => (
          <div key={s.label} style={{ background: '#f5f5f3', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>
      ) : (
        <>
          {/* Drivers section */}
          <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, overflow: 'hidden' }}>
            {data?.drivers?.length === 0 && dispatcherHours.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#999', fontSize: 13 }}>
                No payroll data for this week
              </div>
            ) : (
              <>
                {data?.drivers?.map(driver => (
                  <DriverRow
                    key={driver.driver_id}
                    driver={driver}
                    expanded={!!expanded[driver.driver_id]}
                    onToggle={() => toggleExpanded(driver.driver_id)}
                    weekStart={format(weekStart, 'yyyy-MM-dd')}
                    weekEnd={format(weekEnd, 'yyyy-MM-dd')}
                    onRefresh={fetchPayroll}
                  />
                ))}

                {/* Dispatcher hours section */}
                {dispatcherHours.length > 0 && (
                  <>
                    <div style={{
                      padding: '8px 14px', background: '#f8f8f8',
                      borderTop: '0.5px solid #e0e0db',
                      fontSize: 11, color: '#999', textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>
                      Dispatch Staff
                    </div>
                    {dispatcherHours.map(dh => (
                      <div key={dh.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 14px', borderBottom: '0.5px solid #f0f0f0',
                      }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: '#E6F1FB',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 500, color: '#185FA5',
                        }}>
                          {(dh.user_email || '')[0]?.toUpperCase() || '?'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>
                            {dh.person_name || dh.user_email.split('@')[0].replace(/\./g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                            <Badge bg="#FFF3E0" color="#E65100" label="Dispatcher" />
                          </div>
                          <div style={{ fontSize: 11, color: '#999', marginTop: 1 }}>
                            {dh.hours} hrs @ ${parseFloat(dh.hourly_rate)}/hr
                            {dh.notes && ` · ${dh.notes}`}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 14, fontWeight: 500, color: '#185FA5' }}>
                            {currency(dh.total_pay)}
                          </div>
                          <div style={{ fontSize: 10, color: '#999' }}>dispatcher</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '12px 14px', background: '#f5f5f3',
                  borderTop: '0.5px solid #e0e0db'
                }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Total payout</span>
                  <span style={{ fontSize: 14, fontWeight: 500, color: '#A32D2D' }}>
                    {currency(totalPayout)}
                  </span>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Export buttons */}
      {!loading && (activeDrivers.length > 0 || dispatcherHours.length > 0) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={handleExportNacha} disabled={nachaLoading}
            style={{
              fontSize: 13, padding: '8px 16px', background: '#1D6F42', color: 'white',
              border: 'none', borderRadius: 8, fontWeight: 500,
              opacity: nachaLoading ? 0.6 : 1,
            }}>
            {nachaLoading ? 'Generating...' : 'Export ACH (NACHA)'}
          </button>
          <button onClick={async () => {
            try {
              const dateStr = format(weekStart, 'yyyy-MM-dd');
              const res = await api.get('/quickbooks/payroll-export?date=' + dateStr);
              const blob = new Blob([res.data.csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url;
              a.download = 'payroll-' + dateStr + '.csv'; a.click();
            } catch (err) { alert('Export failed'); }
          }} style={{ fontSize: 13, padding: '8px 16px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, fontWeight: 500 }}>
            Export CSV
          </button>
          <button onClick={() => window.print()}
            style={{ fontSize: 13, padding: '8px 16px', background: '#f0f0f0', border: '0.5px solid #e0e0db', borderRadius: 8 }}>
            Print
          </button>
        </div>
      )}
    </div>
  );
}
